import React from "react";
import { Check } from "lucide-react";
import { AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle, ProgressBar, cn } from "../ui";

/**
 * SetupSteps — "do this, then this": a short numbered list where each step
 * ticks itself off from what already exists, and the next one to do carries
 * its buttons. The Quiz Center's setup and an empty question bank both use it.
 *
 *   steps    [{ key, title, text, done, status? }] — from quizBankRules
 *   current  index of the next step to do, -1 when all are done
 *   actions  { [stepKey]: node } — shown on the current step, and on a done
 *            step only when `actionsOnDone` names it
 */
export default function SetupSteps({ title, description, steps, current, actions = {}, actionsOnDone = [], headerAction }) {
  const done = steps.filter((s) => s.done).length;
  return (
    <AppCard as="section" aria-label={title}>
      <AppCardHeader className="tw-flex-row tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
          <AppCardTitle as="h2">{title}</AppCardTitle>
          {description && <AppCardDescription>{description}</AppCardDescription>}
        </div>
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-flex tw-w-28 tw-flex-col tw-gap-1 tw-text-right tw-text-xs tw-text-content-muted">
            {done} of {steps.length} done
            <ProgressBar value={Math.round((done / steps.length) * 100)} color="primary" />
          </div>
          {headerAction}
        </div>
      </AppCardHeader>
      <AppCardContent>
        <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-p-0">
          {steps.map((s, i) => {
            const isCurrent = i === current;
            const showActions = actions[s.key] && (isCurrent || (s.done && actionsOnDone.includes(s.key)));
            return (
              <li
                key={s.key}
                aria-current={isCurrent ? "step" : undefined}
                className="tw-flex tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-py-3.5 first:tw-border-t-0 first:tw-pt-0 last:tw-pb-0"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "tw-mt-px tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-text-xs tw-font-semibold",
                    s.done && "tw-border-success tw-bg-success tw-text-on-status",
                    !s.done && isCurrent && "tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg",
                    !s.done && !isCurrent && "tw-border-line tw-bg-surface-raised tw-text-content-muted",
                  )}
                >
                  {s.done ? <Check size={14} strokeWidth={3} /> : i + 1}
                </span>
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                  <h3 className={cn("tw-m-0 tw-text-sm tw-text-content", isCurrent ? "tw-font-semibold" : "tw-font-medium")}>
                    {s.title}
                    {s.done && <span className="tw-sr-only"> — done</span>}
                  </h3>
                  <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">{s.text}</p>
                  {s.done && s.status && <p className="tw-m-0 tw-text-xs tw-font-medium tw-text-success-fg">{s.status}</p>}
                  {showActions && <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-2">{actions[s.key]}</div>}
                </div>
              </li>
            );
          })}
        </ol>
      </AppCardContent>
    </AppCard>
  );
}
